import React, { useEffect, useRef, useState } from "react";
import {
  CardHeader,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Menu,
  MenuItem,
} from "@mui/material";
import { observer } from "mobx-react";
import RxRowOverview from "../../../../PatientOverview/RxInfo/RxRowOverview";
import RxFormsData from "../../../../PatientOverview/RxInfo/RxForms/RxFormsData";
import DialogBoxAppointment from "../../../../PrintAppointment/printRxDialogBox";
import TicketForm from "../../../../Ticketing/TicketEditCreate/ticketForm";
import { TerminViewConstants } from "../../../../PatientOverview/TerminView";
import TerminView from "../../../../PatientOverview/RxInfo/TerminView";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import LineItemViewer from "../../../../../molecules/LineItemViewer";
import { useCopyToContinuous } from "../../../../../stores/Rx/rx.mutations";
import { useAppointmentsByRx } from "../../../../../stores/Appointment/appointment.queries";
import CreateTicketForm from "../../../../Ticketing/TicketEditCreate/createTicketForm";

interface TransactionRxOverviewDetailsProps {
  rowData: any;
  editRxMode: boolean;
  setEditRxMode: (val: boolean) => void;
  masterData: any;
  onRxUpdate?: (rxId: string) => void;
}

const TransactionRxOverview: React.FC<TransactionRxOverviewDetailsProps> =
  observer(({ rowData, editRxMode, setEditRxMode, onRxUpdate }) => {
    const [menu, setMenu] = useState<null | HTMLElement>(null);
    const [rxViewLoading, setRxViewLoading] = useState(false);
    const [openTicket, setOpenTicket] = useState<any>(false);
    const [showDialogBoxAppointment, setShowDialogBoxAppointment] =
      useState<boolean>(false);

    const isInitialLoad = useRef(true);

    const { mutate: copyToContinous } = useCopyToContinuous();

    const handleShowAppointment = () => {
      setShowDialogBoxAppointment(true);
    };

    const handleHideAppointment = () => {
      setShowDialogBoxAppointment(false);
    };

    const handleClose = () => {
      if (setEditRxMode) {
        setEditRxMode(false);
      }
    };

    const handleMenuClose = () => {
      setMenu(null);
    };
    const rxTarifGroupId = rowData?.tarifGroup?.id || null;
    const rxDate = rowData?.date;

    const handleCreateVirtualPrescription = async () => {
      try {
        setRxViewLoading(true);
        // Make an API call to create a virtual prescription for the selected row
        const response = copyToContinous(rowData.id, rowData.patient.id);
        handleMenuClose();
        // Concatenate the new values to the 'rxs' array in patientData
        const updatedRxs = [
          ...(rowData.patient?.rxs || []), // Provide a default empty array if patientData?.rxs is undefined
          response, // Replace 'response' with the actual new value
        ];

        // Create a new object with the updated 'rxs' array
        const updatedPatientData: any = {
          ...rowData.patient, // Copy the existing patientData properties
          rxs: updatedRxs, // Update the 'rxs' property with the concatenated array
        };
      } catch (error) {
        console.error("Error creating virtual prescription:", error);
      } finally {
        setRxViewLoading(false);
      }
    };

    const handleCloseTicketForm = () => {
      setOpenTicket(false);
    };

    // Use the query hook to fetch appointments
    const {
      data: appointment = [],
      isLoading: appointmentsLoading,
      error,
    } = useAppointmentsByRx(rowData.id || "");

    // Process the appointments data using useMemo to prevent unnecessary re-calculations
    const appointments = React.useMemo(() => {
      if (error) {
        console.error("Error fetching appointments:", error);
        return [];
      }
      if (!appointment || appointment.length === 0) {
        return [];
      }
      return appointment.filter((app: any) => app.start !== null);
    }, [appointment, error]);

    useEffect(() => {
      if (rowData.patient && rowData) {
        isInitialLoad.current = false;
      }
    }, [rowData.patient, rowData]);

    return (
      <Grid container spacing={2}>
        <Grid item xs={8.5}>
          {rxViewLoading ? (
            <CircularProgress />
          ) : rowData && rowData.patient ? (
            <>
              <RxRowOverview
                editRxMode={editRxMode}
                setEditRxMode={setEditRxMode}
                patientId={rowData.patient.id}
                selectedRxId={rowData.id}
              />

              {editRxMode && (
                <RxFormsData
                  isEditing={true}
                  selectedRxId={rowData.id}
                  selectedPatientId={rowData.patient?.id}
                  openRx={editRxMode}
                  onCloseRx={handleClose}
                  onRxSave={onRxUpdate}
                  useDialog={true}
                />
              )}

              <Menu
                anchorEl={menu}
                open={Boolean(menu)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={handleCreateVirtualPrescription}
                  disabled={rowData.isPerpetual}
                >
                  Dauerrezept anlegen
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setOpenTicket(!openTicket);
                    setMenu(null);
                  }}
                >
                  Ticket erstellen
                </MenuItem>
                <MenuItem onClick={handleShowAppointment}>
                  Termin Ausdrucken
                </MenuItem>
                {showDialogBoxAppointment && (
                  <DialogBoxAppointment
                    id={rowData.rxNumber}
                    openDialog={showDialogBoxAppointment}
                    handleOnClose={handleHideAppointment}
                    single={true}
                    tarifId={rxTarifGroupId}
                    rxDate={rxDate}
                  />
                )}
              </Menu>

              {openTicket && (
                <CreateTicketForm
                  openTicket={openTicket}
                  onCloseTicketForm={handleCloseTicketForm}
                  data={{
                    rxId: rowData.id,
                    rx: rowData,
                    patientId: rowData.patient.id,
                    patient: rowData.patient,
                  }}
                />
              )}
            </>
          ) : (
            <CircularProgress />
          )}
        </Grid>
        <Grid item xs={3.5}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Positionen" />
                <CardContent>
                  <LineItemViewer rxId={rowData.id} autoload={true} />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12}>
              <Card>
                <CardHeader title="Termine" />
                <CardContent>
                  {/* To check the need */}
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <TerminView
                      appointments={appointments}
                      patientId={rowData.patient?.id}
                      appointmentsLoading={appointmentsLoading}
                      isView={TerminViewConstants.RX_VIEW}
                    />
                  </LocalizationProvider>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  });

export default TransactionRxOverview;
